import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { updateSetting, getSettings } from '../services/database';
import { syncStatusBarWithBackground } from '../utils/nativeBridge';

const ThemeContext = createContext(null);

// Theme definitions with earthly, book-like aesthetics
export const THEMES = {
  light: {
    name: 'Parchment',
    description: 'Warm, book-like reading experience',
    colors: {
      // Backgrounds
      background: '#F5F0E8',
      backgroundSecondary: '#EDE6DA',
      backgroundTertiary: '#E5DBC9',
      surface: '#FDFBF7',
      surfaceElevated: '#FFFFFF',
      
      // Text
      textPrimary: '#3D3D3D',
      textSecondary: '#5D5D5D',
      textTertiary: '#7D7D7D',
      textMuted: '#9D9D9D',
      
      // Accent colors (subtle, earthly)
      accent: '#8B7355',
      accentLight: '#A69076',
      accentDark: '#6B5545',
      accentMuted: 'rgba(139, 115, 85, 0.15)',
      
      // Functional colors
      highlight: 'rgba(218, 165, 32, 0.3)',
      highlightYellow: 'rgba(218, 165, 32, 0.3)',
      highlightGreen: 'rgba(107, 142, 35, 0.3)',
      highlightBlue: 'rgba(100, 149, 237, 0.3)',
      highlightPink: 'rgba(219, 112, 147, 0.3)',
      
      // Borders
      border: '#D4C9B8',
      borderLight: '#E8E0D0',
      borderDark: '#C4B9A8',
      
      // Shadows
      shadow: 'rgba(93, 78, 55, 0.08)',
      shadowDark: 'rgba(93, 78, 55, 0.15)',
      
      // Status
      success: '#6B8E23',
      warning: '#DAA520',
      error: '#CD5C5C',
      info: '#6B8BB3',
      
      // Primary (for buttons)
      primary: '#C9A86C',
      primaryDark: '#A98850'
    }
  },
  sepia: {
    name: 'Sepia',
    description: 'Antique manuscript feel',
    colors: {
      background: '#F4ECD8',
      backgroundSecondary: '#E8DFC8',
      backgroundTertiary: '#DCD0B5',
      surface: '#FAF5E8',
      surfaceElevated: '#FFFCF5',
      
      textPrimary: '#433422',
      textSecondary: '#5D4832',
      textTertiary: '#776048',
      textMuted: '#8B7355',
      
      accent: '#8B6914',
      accentLight: '#A68B30',
      accentDark: '#6B4914',
      accentMuted: 'rgba(139, 105, 20, 0.15)',
      
      highlight: 'rgba(205, 133, 63, 0.35)',
      highlightYellow: 'rgba(218, 165, 32, 0.35)',
      highlightGreen: 'rgba(107, 142, 35, 0.35)',
      highlightBlue: 'rgba(100, 149, 237, 0.35)',
      highlightPink: 'rgba(219, 112, 147, 0.35)',
      
      border: '#D4C4A8',
      borderLight: '#E8DCC8',
      borderDark: '#C4B498',
      
      shadow: 'rgba(67, 52, 34, 0.1)',
      shadowDark: 'rgba(67, 52, 34, 0.2)',
      
      success: '#6B8E23',
      warning: '#DAA520',
      error: '#CD5C5C',
      info: '#6B8BB3',
      
      primary: '#C9A86C',
      primaryDark: '#A98850'
    }
  },
  dark: {
    name: 'Evening',
    description: 'Gentle dark mode for night reading',
    colors: {
      background: '#2C2825',
      backgroundSecondary: '#363230',
      backgroundTertiary: '#403C38',
      surface: '#38342F',
      surfaceElevated: '#444038',
      
      textPrimary: '#E8E0D8',
      textSecondary: '#C8C0B8',
      textTertiary: '#A8A098',
      textMuted: '#888078',
      
      accent: '#C9A86C',
      accentLight: '#D9B87C',
      accentDark: '#A98850',
      accentMuted: 'rgba(201, 168, 108, 0.2)',
      
      highlight: 'rgba(218, 165, 32, 0.25)',
      highlightYellow: 'rgba(218, 165, 32, 0.25)',
      highlightGreen: 'rgba(107, 142, 35, 0.25)',
      highlightBlue: 'rgba(100, 149, 237, 0.25)',
      highlightPink: 'rgba(219, 112, 147, 0.25)',
      
      border: '#4A4540',
      borderLight: '#5A5550',
      borderDark: '#3A3530',
      
      shadow: 'rgba(0, 0, 0, 0.2)',
      shadowDark: 'rgba(0, 0, 0, 0.35)',
      
      success: '#8FBC8F',
      warning: '#F0C040',
      error: '#E07070',
      info: '#87CEEB',
      
      primary: '#C9A86C',
      primaryDark: '#A98850'
    }
  },
  
  // Color Hunt inspired themes
  ocean: {
    name: 'Ocean',
    description: 'Calm navy and teal tones',
    colors: {
      background: '#E8F0F2',
      backgroundSecondary: '#C4DFE6',
      backgroundTertiary: '#A8D0DB',
      surface: '#F0F7F9',
      surfaceElevated: '#FFFFFF',
      
      textPrimary: '#2C3E50',
      textSecondary: '#446680',
      textTertiary: '#6889A5',
      textMuted: '#8EAABB',
      
      accent: '#088DA5',
      accentLight: '#17B5D8',
      accentDark: '#066D80',
      accentMuted: 'rgba(8, 141, 165, 0.15)',
      
      highlight: 'rgba(8, 141, 165, 0.25)',
      highlightYellow: 'rgba(240, 200, 80, 0.3)',
      highlightGreen: 'rgba(72, 201, 176, 0.3)',
      highlightBlue: 'rgba(8, 141, 165, 0.3)',
      highlightPink: 'rgba(219, 112, 147, 0.3)',
      
      border: '#B0CDD6',
      borderLight: '#D0E5EB',
      borderDark: '#90B5C0',
      
      shadow: 'rgba(44, 62, 80, 0.08)',
      shadowDark: 'rgba(44, 62, 80, 0.15)',
      
      success: '#48C9B0',
      warning: '#F0C850',
      error: '#E07070',
      info: '#5DADE2',
      
      primary: '#17B5D8',
      primaryDark: '#088DA5'
    }
  },
  forest: {
    name: 'Forest',
    description: 'Deep greens and olive tones',
    colors: {
      background: '#F5F7F0',
      backgroundSecondary: '#E3E8D8',
      backgroundTertiary: '#D1D9C0',
      surface: '#FAFBF7',
      surfaceElevated: '#FFFFFF',
      
      textPrimary: '#2D3A25',
      textSecondary: '#4A5D3E',
      textTertiary: '#6B8058',
      textMuted: '#8FA07A',
      
      accent: '#5D7A4A',
      accentLight: '#7A9B62',
      accentDark: '#465C38',
      accentMuted: 'rgba(93, 122, 74, 0.15)',
      
      highlight: 'rgba(93, 122, 74, 0.25)',
      highlightYellow: 'rgba(200, 180, 80, 0.3)',
      highlightGreen: 'rgba(93, 122, 74, 0.35)',
      highlightBlue: 'rgba(100, 149, 180, 0.3)',
      highlightPink: 'rgba(180, 120, 140, 0.3)',
      
      border: '#C4D0B5',
      borderLight: '#DDE5D0',
      borderDark: '#A8B898',
      
      shadow: 'rgba(45, 58, 37, 0.08)',
      shadowDark: 'rgba(45, 58, 37, 0.15)',
      
      success: '#6B9B5A',
      warning: '#C9A84A',
      error: '#B85C5C',
      info: '#6890A0',
      
      primary: '#7A9B62',
      primaryDark: '#5D7A4A'
    }
  },
  sunset: {
    name: 'Sunset',
    description: 'Warm coral and peach hues',
    colors: {
      background: '#FEF6F0',
      backgroundSecondary: '#FDE8DB',
      backgroundTertiary: '#FCD9C5',
      surface: '#FFFAF7',
      surfaceElevated: '#FFFFFF',
      
      textPrimary: '#4A3535',
      textSecondary: '#6B4F4F',
      textTertiary: '#8C6A6A',
      textMuted: '#A88888',
      
      accent: '#E07B65',
      accentLight: '#F09580',
      accentDark: '#C66050',
      accentMuted: 'rgba(224, 123, 101, 0.15)',
      
      highlight: 'rgba(224, 123, 101, 0.25)',
      highlightYellow: 'rgba(248, 200, 100, 0.3)',
      highlightGreen: 'rgba(130, 180, 130, 0.3)',
      highlightBlue: 'rgba(130, 170, 200, 0.3)',
      highlightPink: 'rgba(224, 123, 101, 0.35)',
      
      border: '#F0D5C8',
      borderLight: '#F8E8E0',
      borderDark: '#E0C0B0',
      
      shadow: 'rgba(74, 53, 53, 0.08)',
      shadowDark: 'rgba(74, 53, 53, 0.15)',
      
      success: '#82B882',
      warning: '#E8B850',
      error: '#D86060',
      info: '#82AAC8',
      
      primary: '#F09580',
      primaryDark: '#E07B65'
    }
  },
  sage: {
    name: 'Sage',
    description: 'Soft sage and mint tones',
    colors: {
      background: '#F0F5F0',
      backgroundSecondary: '#DCE8DC',
      backgroundTertiary: '#C8DBC8',
      surface: '#F7FAF7',
      surfaceElevated: '#FFFFFF',
      
      textPrimary: '#3A453A',
      textSecondary: '#556555',
      textTertiary: '#708570',
      textMuted: '#90A590',
      
      accent: '#6B9B7A',
      accentLight: '#88B898',
      accentDark: '#508060',
      accentMuted: 'rgba(107, 155, 122, 0.15)',
      
      highlight: 'rgba(107, 155, 122, 0.25)',
      highlightYellow: 'rgba(200, 190, 100, 0.3)',
      highlightGreen: 'rgba(107, 155, 122, 0.35)',
      highlightBlue: 'rgba(100, 150, 180, 0.3)',
      highlightPink: 'rgba(180, 130, 150, 0.3)',
      
      border: '#BDD0BD',
      borderLight: '#D8E8D8',
      borderDark: '#A0B8A0',
      
      shadow: 'rgba(58, 69, 58, 0.08)',
      shadowDark: 'rgba(58, 69, 58, 0.15)',
      
      success: '#6BAA70',
      warning: '#C8A850',
      error: '#C06060',
      info: '#70A0B8',
      
      primary: '#88B898',
      primaryDark: '#6B9B7A'
    }
  },
  midnight: {
    name: 'Midnight',
    description: 'Deep blue night mode',
    colors: {
      background: '#1A2332',
      backgroundSecondary: '#243040',
      backgroundTertiary: '#2E3D50',
      surface: '#202838',
      surfaceElevated: '#2A3848',
      
      textPrimary: '#E0E8F0',
      textSecondary: '#B8C8D8',
      textTertiary: '#90A8C0',
      textMuted: '#6888A8',
      
      accent: '#5DADE2',
      accentLight: '#85C1E9',
      accentDark: '#3498DB',
      accentMuted: 'rgba(93, 173, 226, 0.2)',
      
      highlight: 'rgba(93, 173, 226, 0.25)',
      highlightYellow: 'rgba(240, 200, 100, 0.25)',
      highlightGreen: 'rgba(100, 200, 150, 0.25)',
      highlightBlue: 'rgba(93, 173, 226, 0.3)',
      highlightPink: 'rgba(200, 130, 160, 0.25)',
      
      border: '#3A4858',
      borderLight: '#4A5868',
      borderDark: '#2A3848',
      
      shadow: 'rgba(0, 0, 0, 0.25)',
      shadowDark: 'rgba(0, 0, 0, 0.4)',
      
      success: '#58D68D',
      warning: '#F4D03F',
      error: '#EC7063',
      info: '#5DADE2',
      
      primary: '#5DADE2',
      primaryDark: '#3498DB'
    }
  },
  blackout: {
    name: 'Jordan',
    description: 'High contrast black with white text',
    colors: {
      background: '#000000',
      backgroundSecondary: '#0A0A0A',
      backgroundTertiary: '#141414',
      surface: '#0A0A0A',
      surfaceElevated: '#1A1A1A',
      
      textPrimary: '#FFFFFF',
      textSecondary: '#E0E0E0',
      textTertiary: '#B0B0B0',
      textMuted: '#808080',
      
      accent: '#444444',
      accentLight: '#555555',
      accentDark: '#333333',
      accentMuted: 'rgba(255, 255, 255, 0.1)',
      
      highlight: 'rgba(255, 255, 255, 0.2)',
      highlightYellow: 'rgba(255, 230, 100, 0.3)',
      highlightGreen: 'rgba(100, 255, 150, 0.25)',
      highlightBlue: 'rgba(100, 180, 255, 0.25)',
      highlightPink: 'rgba(255, 150, 200, 0.25)',
      
      border: '#333333',
      borderLight: '#444444',
      borderDark: '#222222',
      
      shadow: 'rgba(0, 0, 0, 0.5)',
      shadowDark: 'rgba(0, 0, 0, 0.7)',
      
      success: '#00FF88',
      warning: '#FFCC00',
      error: '#FF5555',
      info: '#55AAFF',
      
      primary: '#333333',
      primaryDark: '#222222'
    }
  }
};

// Font size presets
export const FONT_SIZES = {
  small: {
    name: 'Small',
    base: 14,
    scale: 0.875
  },
  medium: {
    name: 'Medium',
    base: 16,
    scale: 1
  },
  large: {
    name: 'Large',
    base: 18,
    scale: 1.125
  },
  xlarge: {
    name: 'Extra Large',
    base: 20,
    scale: 1.25
  }
};

// Font family presets
export const FONT_FAMILIES = {
  merriweather: {
    name: 'Merriweather',
    family: "'Merriweather', Georgia, 'Times New Roman', serif",
    description: 'Classic serif for comfortable reading'
  },
  georgia: {
    name: 'Georgia',
    family: "Georgia, 'Times New Roman', serif",
    description: 'Traditional book typeface'
  },
  sourceSans: {
    name: 'Source Sans',
    family: "'Source Sans 3', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    description: 'Clean sans-serif for modern feel'
  },
  system: {
    name: 'System',
    family: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    description: 'Native system font'
  }
};

// Line spacing presets
export const LINE_SPACINGS = {
  compact: {
    name: 'Compact',
    value: 1.4
  },
  comfortable: {
    name: 'Comfortable',
    value: 1.7
  },
  relaxed: {
    name: 'Relaxed',
    value: 2.0
  }
};

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState('blackout');
  const [fontSize, setFontSizeState] = useState('medium');
  const [fontFamily, setFontFamilyState] = useState('merriweather');
  const [lineSpacing, setLineSpacingState] = useState('comfortable');
  const [isLoading, setIsLoading] = useState(true);
  
  // Load saved preferences
  useEffect(() => {
    async function loadPreferences() {
      try {
        const settings = await getSettings(['theme', 'fontSize', 'fontFamily', 'lineSpacing']);
        
        if (settings.theme && THEMES[settings.theme]) {
          setThemeState(settings.theme);
        }
        if (settings.fontSize && FONT_SIZES[settings.fontSize]) {
          setFontSizeState(settings.fontSize);
        }
        if (settings.fontFamily && FONT_FAMILIES[settings.fontFamily]) {
          setFontFamilyState(settings.fontFamily);
        }
        if (settings.lineSpacing && LINE_SPACINGS[settings.lineSpacing]) {
          setLineSpacingState(settings.lineSpacing);
        }
      } catch (err) {
        console.error('Failed to load theme preferences:', err);
      } finally {
        setIsLoading(false);
      }
    }
    
    loadPreferences();
  }, []);
  
  // Apply CSS variables when theme changes
  useEffect(() => {
    const root = document.documentElement;
    const colors = THEMES[theme].colors;
    const fontConfig = FONT_SIZES[fontSize];
    const fontFamilyConfig = FONT_FAMILIES[fontFamily];
    const lineSpacingConfig = LINE_SPACINGS[lineSpacing];
    
    // Apply colors with --color- prefix
    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });
    
    // Apply legacy color variable mappings for backward compatibility
    root.style.setProperty('--bg-color', colors.background);
    root.style.setProperty('--bg-secondary', colors.backgroundSecondary);
    root.style.setProperty('--bg-hover', colors.backgroundTertiary);
    root.style.setProperty('--text-color', colors.textPrimary);
    root.style.setProperty('--text-muted', colors.textMuted);
    root.style.setProperty('--text-secondary', colors.textSecondary);
    root.style.setProperty('--border-color', colors.border);
    root.style.setProperty('--primary-color', colors.accent);
    root.style.setProperty('--accent-color', colors.accent);
    
    // Apply typography
    root.style.setProperty('--font-size-base', `${fontConfig.base}px`);
    root.style.setProperty('--font-scale', fontConfig.scale);
    root.style.setProperty('--font-family', fontFamilyConfig.family);
    root.style.setProperty('--line-height', lineSpacingConfig.value);
    
    // Apply to body
    document.body.style.backgroundColor = colors.background;
    document.body.style.color = colors.textPrimary;
    syncStatusBarWithBackground(colors.background);
  }, [theme, fontSize, fontFamily, lineSpacing]);
  
  // Theme setter with persistence
  const setTheme = useCallback(async (newTheme) => {
    if (THEMES[newTheme]) {
      setThemeState(newTheme);
      await updateSetting('theme', newTheme);
    }
  }, []);
  
  const setFontSize = useCallback(async (newSize) => {
    if (FONT_SIZES[newSize]) {
      setFontSizeState(newSize);
      await updateSetting('fontSize', newSize);
    }
  }, []);
  
  const setFontFamily = useCallback(async (newFamily) => {
    if (FONT_FAMILIES[newFamily]) {
      setFontFamilyState(newFamily);
      await updateSetting('fontFamily', newFamily);
    }
  }, []);
  
  const setLineSpacing = useCallback(async (newSpacing) => {
    if (LINE_SPACINGS[newSpacing]) {
      setLineSpacingState(newSpacing);
      await updateSetting('lineSpacing', newSpacing);
    }
  }, []);
  
  // Computed values
  const currentTheme = THEMES[theme];
  const currentFontSize = FONT_SIZES[fontSize];
  const currentFontFamily = FONT_FAMILIES[fontFamily];
  const currentLineSpacing = LINE_SPACINGS[lineSpacing];
  
  const value = {
    // Current values
    theme,
    fontSize,
    fontFamily,
    lineSpacing,
    
    // Computed
    colors: currentTheme.colors,
    fontSizeBase: currentFontSize.base,
    fontFamilyValue: currentFontFamily.family,
    lineSpacingValue: currentLineSpacing.value,
    
    // Setters
    setTheme,
    setFontSize,
    setFontFamily,
    setLineSpacing,
    
    // All options
    themes: THEMES,
    fontSizes: FONT_SIZES,
    fontFamilies: FONT_FAMILIES,
    lineSpacings: LINE_SPACINGS,
    
    // Loading state
    isLoading
  };
  
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
